import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, Redirect } from 'react-router-dom';
import { completeTarget, getTargetList } from 'config/api';
import * as consts from 'config/consts';
import { useModal } from 'hooks/useModal';
import { usePremium } from 'hooks/usePremium';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useScroll } from 'hooks/useScroll';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { isIosApp } from 'components/features/utils/userStatus';
import { useTargetSupportRouting } from '../hooks/useTargetSupportRouting';
import { Target, TargetId, TargetModalStatus, TargetType } from '../utils/type';
import { useBirthday } from '../utils/birthday';
import { NotFoundBirthday } from '../components/features/birthday/NotFoundBirthday';
import { TargetListLoading } from '../components/features/index/TargetListLoading';
import { TargetList } from '../components/features/index/TargetList';
import { TargetItem } from '../components/features/index/TargetItem';
import { TargetListEmpty } from '../components/features/index/TargetListEmpty';
import { TargetPremium } from '../components/features/TargetPremium';
import { EndTargetModal } from '../components/features/modal/EndTargetModal';

const TargetIndexInternal = ({
  targets,
  birthday,
}: {
  targets: Target[];
  birthday: Date;
}) => {
  const { setActionPattern } = useCustomizeHeaderContext();
  const {
    goTargetPushDetail,
    goTargetPushSelect,
    goSetTitle,
    goSetAmount,
  } = useTargetSupportRouting();
  const history = useHistory();

  useEffect(() => {
    setActionPattern(consts.HeaderActionPatternType.なし);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCustomHeaderTitle('資産目標サポート');

  const [contextMenuOpenedId, setContextMenuOpenedId] = useState<
    { tag: 'Opened'; id: TargetId } | { tag: 'NotOpened' }
  >({ tag: 'NotOpened' });
  const [endTargetId, setEndTargetId] = useState<string | null>(null);
  const [isShow, { hideModal, showModal }] = useModal();

  const openContextMenu = (targetId: TargetId) => (): void => {
    setContextMenuOpenedId({ tag: 'Opened', id: targetId });
  };

  const hideContextMenu = (): void => {
    setContextMenuOpenedId({ tag: 'NotOpened' });
  };

  const endTarget = async (): Promise<void> => {
    if (endTargetId === null) return;
    await completeTargetAPI(endTargetId);
    history.go(0);
  };

  const showEndTargetModal = (id: string) => {
    setEndTargetId(id);
    showModal();
  };

  useScroll();

  return (
    <>
      {targets.length ? (
        <>
          <TargetList
            {...{
              addTarget: goTargetPushSelect,
              targetCount: targets.length,
            }}>
            {targets.map(target => {
              return (
                <TargetItem
                  {...{
                    target,
                    goEditTargetAmount: () => {
                      goSetAmount(target.id);
                    },
                    goEditTitle: (): void => {
                      goSetTitle(target.id);
                    },
                    goTargetDetail: () => goTargetPushDetail(target.id),
                    endTarget: (): void => showEndTargetModal(target.id),
                    isOpenContextMenu:
                      contextMenuOpenedId.tag === 'Opened' &&
                      target.id === contextMenuOpenedId.id,
                    hideContextMenu,
                    openContextMenu: openContextMenu(target.id),
                    birthday,
                  }}
                  key={target.id}
                />
              );
            })}
          </TargetList>
          <EndTargetModal
            {...{
              endTarget,
              hide: hideModal,
              isShow: isShow,
            }}
          />
        </>
      ) : (
        <TargetListEmpty {...{ addTarget: goTargetPushSelect }} />
      )}
    </>
  );
};

const GetTargetData = () => {
  const { goTargetPushSelect } = useTargetSupportRouting();

  const { data, isFetching } = useQuery(
    'targetIndexTargetList',
    () => getTargetListAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { birthday, isFetched: isBirthdayFetched } = useBirthday();

  if (isFetching || !isBirthdayFetched) return <TargetListLoading />;

  if (birthday === null || birthday === undefined) return <NotFoundBirthday />;

  if (data === null || data === undefined)
    return (
      <>
        <TargetListEmpty {...{ addTarget: goTargetPushSelect }} />
      </>
    );

  return <TargetIndexInternal {...{ targets: data, birthday }} />;
};

const TargetIndex = (): React.ReactElement => {
  const [premiumStatus] = usePremium();
  const { setWithBackIconWord } = useCustomizeHeaderContext();

  useEffect(() => {
    setWithBackIconWord(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isIosApp()) return <Redirect to="/home" />;

  return premiumStatus.status !== 'loading' ? (
    <>
      {premiumStatus.status === 'isNotPremium' && <TargetPremium />}
      {premiumStatus.status === 'isPremium' && <GetTargetData />}
    </>
  ) : (
    <TargetListLoading />
  );
};

const getTargetListAPI = async () => {
  const res_ = await getTargetList();

  if (res_.Items === null) return null;

  const res = res_.Items.map(item => {
    return {
      id: item.ID,
      title: item.Title,
      refType: item.RefType as TargetType,
      startDate: item.StartDate,
      endDate: item.EndDate,
      targetAmount: item.TargetAmount,
      accountList: item.AccountList.map(list => {
        return {
          account: list.Account,
          brokerName: list.BrokerName,
          available: list.Available,
          totalAssetAmount: list.TotalAssetAmount,
        };
      }),
      excludeAmount: item.ExcludeAmount,
      reserveAmount: item.ReserveAmount,
      assetAmount: item.AssetAmount,
      yield: item.Yield,
      achieved: item.Achieved,
      status: item.Status as TargetModalStatus,
    };
  });

  return res as Target[];
};

const completeTargetAPI = async (id: string): Promise<void> => {
  await completeTarget(id);
};

export default TargetIndex;
